import luhn from 'fast-luhn'
import { DateTime } from 'luxon'

/*
Basic validators
 */

function isEmail(value: string): boolean {
  return /^[a-zA-ZåäöÅÄÖ0-9._+-]+@[a-zA-ZåäöÅÄÖ0-9.-]+\.[a-zA-ZåäöÅÄÖ]{2,}$/.test(
    value,
  )
}

function isPayexEmail(value: string): boolean {
  if (value?.endsWith('.')) return false
  return /^[^;\r\n\xA0\xD0\x96\─@<>]+@(?!\.)(?!.*\.\.)(?:[^;\r\n\xA0\xD0\x96\─@<>]+\.[^;\r\n\xA0\xD0\x96\─@<>]+|\[[A-Fa-f\d\:]+\])$/.test(
    value,
  )
}

function isPersonalId(value: string): boolean {
  let boo = false
  if (value && /^\d{12}$/i.test(value)) {
    boo = luhn(value.replace(/\D/g, '').substr(-10))
  }
  return boo
}

function isOrganisationNumber(value: string): boolean {
  if (value && /^(\d{2})?\d{10}$/i.test(value)) {
    return luhn(value.replace(/\D/g, '').substr(-10))
  }
  return false
}

function isPositiveInteger(value: any): boolean {
  if (isInteger(value)) {
    const integer = parseInt(value, 10)
    return !isNaN(integer) && integer > 0
  } 
  return false
}

function isInteger(value: any): boolean {
  return /^(-|\d)([ 0-9]*)$/.test(value)
}

function isRequired(value: any): boolean {
  return !!value
}

function isSameValue(value1: any, value2: any): boolean {
  return value1 === value2
}

function isTelephoneNumber(value: any): boolean {
  return !!(`${value}`).replace(/\D/g, '').match(/\d{7,}/)
}

function isSwedishZipCode(value: any = ''): boolean {
  return /^\d{5}$/i.test(value.replace(/\D/g, ''))
}

function isZipCode(value: any = ''): boolean {
  return /^[A-Z0-9-]{1,9}$/i.test(value)
}

function isPatternValid(value: string = null): boolean {
  if (value === null) {
    return true
  }
  return /^[a-zA-Z0-9_:!;"#<>=?\[\]@{}´ %-/À-ÖØ-öø-ú]*$/.test(value)
}

function isOnlyDigits(value: string): boolean {
  if (!value) return false
  return /^[0-9]*$/.test(value)
}

function isAtMostFiftyCharacters(value: string): boolean {
  if (!value) return false
  return value.length && value.length <= 50
}

function isMonthOfDateBeforeAnother(date: DateTime, comparisonDate: DateTime): boolean {
  return date.startOf('month') < comparisonDate.startOf('month')
}

function isDateThisMonthOrLater(date: string): boolean {
  const dateTime = date ? DateTime.fromJSDate(new Date(date)) : null
  const currentDate = DateTime.fromJSDate(new Date())

  if (!dateTime) {
    return true
  }
  return !isMonthOfDateBeforeAnother(dateTime, currentDate)
}

function isDateLastMonthOrLater(date: string): boolean {
  const dateTime = date ? DateTime.fromJSDate(new Date(date)) : null
  const dateLastMonth = DateTime.fromJSDate(new Date()).minus({ month: 1 })

  if (!dateTime) {
    return true
  }

  return !isMonthOfDateBeforeAnother(dateTime, dateLastMonth)
}

function isStartDateSameOrBeforeEndDate(
  value: string,
  allValues: { startDate: string; endDate: string },
): boolean {
  const start = new Date(allValues.startDate)
  const end = allValues.endDate ? new Date(allValues.endDate) : null

  const startDateTime = start ? DateTime.fromJSDate(start) : null
  const endDateTime = end ? DateTime.fromJSDate(end) : null

  if (!endDateTime) {
    return true
  }

  return !isMonthOfDateBeforeAnother(endDateTime, startDateTime)
}

function isReferenceFieldsEmpty(values: { customerRef1: string; customerRef2: string }): boolean {
  return !values.customerRef1 && !values.customerRef2;
}

export {
  isEmail,
  isPayexEmail,
  isPersonalId,
  isOrganisationNumber,
  isPositiveInteger,
  isInteger,
  isRequired,
  isSameValue,
  isTelephoneNumber,
  isSwedishZipCode,
  isPatternValid,
  isOnlyDigits,
  isAtMostFiftyCharacters,
  isStartDateSameOrBeforeEndDate,
  isReferenceFieldsEmpty,
  isZipCode
}

/*
Helper functions
 */

function getFormValidator(
  validator: validatorFunctionType,
  error: any,
  success?: any,
): any {
  return (...values) => (validator(...values) ? success : error)
}

// function getFormValidator(validator: validatorFunctionType, error, success?) {
//   
//   return (value) => (validator(value) ? success : error);
// }

type validatorFunctionType = (...values: any[]) => boolean

/*
Standard form validators
 */

export const validateEmail = getFormValidator(isEmail, 'emailExpected')
export const validatePayexEmail = getFormValidator(isPayexEmail, 'emailExpected')
export const validatePersonalId = getFormValidator(
  isPersonalId,
  'personalIdExpected',
)
export const validateOrgNumber = getFormValidator(
  isOrganisationNumber,
  'orgNumberExpected',
)
export const validatePositiveInteger = getFormValidator(
  isPositiveInteger,
  'positiveIntegerExpected',
)
export const validateWholeNumber = getFormValidator(
  isInteger,
  'wholeNumberExpected',
)
export const validateNumber = getFormValidator(isInteger, 'integerExpected')
export const validateRequired = getFormValidator(isRequired, 'required')
export const validateAnswerRequired = getFormValidator(isRequired, 'answerRequired')
export const validateTelephoneNumber = getFormValidator(
  isTelephoneNumber,
  'telephoneNumberExpected',
)
export const validateSwedishZipCode = getFormValidator(
  isSwedishZipCode,
  'swedishZipCodeExpected',
)
export const validateZipCode = getFormValidator(
  isZipCode,
  'zipCodeExpected',
)
export const validateSameEmail = getFormValidator(
  isSameValue,
  'sameEmailExpected',
)
export const validateCharacters = getFormValidator(
  isPatternValid,
  'invalidCharacter',
)
export const validateAddresseeId = getFormValidator(
  isOnlyDigits,
  'invalidAddresseeId',
)
export const validateAtMostFiftyCharacters = getFormValidator(
  isAtMostFiftyCharacters,
  'atMostFiftyCharacters',
)
export const validateStartDateIsThisMonthOrLater = getFormValidator(
  isDateThisMonthOrLater,
  'start_date_in_the_past',
)
export const validateEndDateIsThisMonthOrLater = getFormValidator(
  isDateThisMonthOrLater,
  'end_date_in_the_past',
)
export const validateEndDateIsLastMonthOrLater = getFormValidator(
  isDateLastMonthOrLater,
  'end_date_last_month_or_future',
)
export const validateStartDateIsBeforeOrTheSameAsEndDate = getFormValidator(
  isStartDateSameOrBeforeEndDate,
  'end_date_before_start_date',
)
